import { computed, type ComputedRef } from 'vue'
import {
  ACCESS_TYPE,
  ACTOR_TYPE,
  MENTOR_STATUS,
  PROFILE_STATUS,
  PROFILE_VISIBILITY,
  ROLE,
  SUBSCRIPTION_BILLING_CYCLE,
  SUBSCRIPTION_GROUP,
  SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STRIPE_CONNECTION_STATUS,
  SUBSCRIPTION_TIER,
  SUBSCRIPTION_TRIAL_INTERVAL,
  SUBSCRIPTION_TYPE,
  TEAM_ROLE,
  type AccessType,
  type ActorType,
  type MentorStatus,
  type ProfileStatus,
  type ProfileVisibility,
  type Role,
  type SubscriptionBillingCycle,
  type SubscriptionGroup,
  type SubscriptionNextBillingPeriodStatus,
  type SubscriptionStatus,
  type SubscriptionStripeConnectionStatus,
  type SubscriptionTier,
  type SubscriptionTrialInterval,
  type SubscriptionType,
  type TeamRole,
} from '@/shared/data/permissionSystem'
import { usePage } from '@inertiajs/vue3'

type FreeCallsPrivilege = {
  limitHit: boolean
  count: number
  currentCount: number
  remainingCount: number
}

type SubscriptionPrivileges = {
  connectionRequests: boolean
  doubleUpQueue: boolean
  filterCommunityMembers: boolean
  opportunitiesPage: boolean
  freeCallsPerMonth: FreeCallsPrivilege & { renewalLimitDate: string }
  freeCalls: FreeCallsPrivilege
  freePaybackCalls: FreeCallsPrivilege
  favoritedReviews: FreeCallsPrivilege
}

type SubscriptionAccess = {
  billingCycle: string
  name: string
  nextBillingPeriodStatus: string
  status: string
  stripeConnection: string
  tier: string
  trialInterval: string
  type: string
  group: string
  privileges: SubscriptionPrivileges
}

type AccessControlValue = {
  accessType: string
  actorType: string
  profileStatus: string
  profileVisibility: string
  role: string
  teamRole: string
  mentorStatus: string
  subscription: SubscriptionAccess
}

type PermissionObject = {
  permissions: string[]
}

type ConstObject = Record<string, string>

export const usePermissionSystem = () => {
  const page = usePage()

  const accessControl: ComputedRef<AccessControlValue> = computed(() => {
    const { accessControl } = page.props.shared.currentUser
    const { subscription } = accessControl
    const { privileges } = subscription
    return {
      accessType: accessControl.accessType,
      actorType: accessControl.actorType,
      profileStatus: accessControl.profileStatus,
      profileVisibility: accessControl.profileVisibility,
      role: accessControl.role,
      teamRole: accessControl.teamRole,
      mentorStatus: accessControl.mentorStatus,
      subscription: {
        billingCycle: subscription.billingCycle,
        name: subscription.name,
        nextBillingPeriodStatus: subscription.nextBillingPeriodStatus,
        status: subscription.status,
        stripeConnection: subscription.stripeConnection,
        tier: subscription.tier,
        trialInterval: subscription.trialInterval,
        type: subscription.type,
        group: subscription.group,
        privileges: {
          connectionRequests: privileges.connectionRequests,
          doubleUpQueue: privileges.doubleUpQueue,
          filterCommunityMembers: privileges.filterCommunityMembers,
          opportunitiesPage: privileges.opportunitiesPage,
          freeCallsPerMonth: {
            limitHit: privileges.freeCallsPerMonth.limitHit,
            count: privileges.freeCallsPerMonth.count,
            currentCount: privileges.freeCallsPerMonth.currentCount,
            remainingCount: privileges.freeCallsPerMonth.remainingCount,
            renewalLimitDate: privileges.renewalLimitDate,
          },
          freeCalls: {
            limitHit: privileges.freeCalls.limitHit,
            count: privileges.freeCalls.count,
            currentCount: privileges.freeCalls.currentCount,
            remainingCount: privileges.freeCalls.remainingCount,
          },
          // Feature description: Tit for tat, 1 call done as mentor = 1 available free call as mentee
          freePaybackCalls: {
            limitHit: privileges.freePaybackCalls.limitHit,
            count: privileges.freePaybackCalls.count,
            currentCount: privileges.freePaybackCalls.currentCount,
            remainingCount: privileges.freePaybackCalls.remainingCount,
          },
          favoritedReviews: {
            limitHit: privileges.favoritedReviews.limitHit,
            count: privileges.favoritedReviews.count,
            currentCount: privileges.favoritedReviews.currentCount,
            remainingCount: privileges.favoritedReviews.remainingCount,
          },
        },
      },
    }
  })

  const isUserLoggedIn = computed(
    () => page.props.shared.currentUser.accessControl.role !== ROLE.GUEST,
  )

  const hasPaidTeam = computed(() => {
    return (
      isSubscriptionStatus(SUBSCRIPTION_STATUS.PAID) &&
      isSubscriptionTier(SUBSCRIPTION_TIER.TEAM)
    )
  })

  const checkAccess = (
    object: ConstObject,
    userValue: keyof Omit<AccessControlValue, 'subscription'>,
    value: string,
  ): boolean => {
    const validValues = Object.values(object)
    if (!validValues.includes(value)) {
      throw new Error(
        `[Permission System - Access] Invalid value. Valid values types are: ${validValues.join(
          ', ',
        )}`,
      )
    }

    return accessControl.value[userValue].toLowerCase() === value.toLowerCase()
  }

  const checkSubscriptionAccess = (
    object: ConstObject,
    subscriptionValue: keyof Omit<SubscriptionAccess, 'privileges'>,
    value: string,
  ): boolean => {
    const validValues = Object.values(object)
    if (!validValues.includes(value)) {
      throw new Error(
        `[Permission System - Subscription] Invalid value. Valid values types are: ${validValues.join(
          ', ',
        )}`,
      )
    }

    return (
      accessControl.value.subscription[subscriptionValue].toLowerCase() ===
      value.toLowerCase()
    )
  }

  const hasAccess = (accessType: AccessType): boolean => {
    return checkAccess(ACCESS_TYPE, 'accessType', accessType)
  }

  const isActorType = (actorType: ActorType): boolean => {
    return checkAccess(ACTOR_TYPE, 'actorType', actorType)
  }

  const isMentorStatus = (mentorStatus: MentorStatus): boolean => {
    return checkAccess(MENTOR_STATUS, 'mentorStatus', mentorStatus)
  }

  const isProfileStatus = (profileStatus: ProfileStatus): boolean => {
    return checkAccess(PROFILE_STATUS, 'profileStatus', profileStatus)
  }

  const isProfileVisibility = (
    profileVisibility: ProfileVisibility,
  ): boolean => {
    return checkAccess(
      PROFILE_VISIBILITY,
      'profileVisibility',
      profileVisibility,
    )
  }

  const hasRole = (role: Role): boolean => {
    return checkAccess(ROLE, 'role', role)
  }

  const hasTeamRole = (teamRole: TeamRole): boolean => {
    return checkAccess(TEAM_ROLE, 'teamRole', teamRole)
  }

  const isSubscriptionBillingCycle = (
    billingCycle: SubscriptionBillingCycle,
  ): boolean => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_BILLING_CYCLE,
      'billingCycle',
      billingCycle,
    )
  }

  const isSubscriptionNextBillingPeriodStatus = (
    nextBillingPeriodStatus: SubscriptionNextBillingPeriodStatus,
  ): boolean => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS,
      'nextBillingPeriodStatus',
      nextBillingPeriodStatus,
    )
  }

  const isSubscriptionStatus = (status: SubscriptionStatus): boolean => {
    return checkSubscriptionAccess(SUBSCRIPTION_STATUS, 'status', status)
  }

  const isSubscriptionStripeConnectionStatus = (
    stripeConnection: SubscriptionStripeConnectionStatus,
  ): boolean => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_STRIPE_CONNECTION_STATUS,
      'stripeConnection',
      stripeConnection,
    )
  }

  const isSubscriptionTier = (tier: SubscriptionTier): boolean => {
    return checkSubscriptionAccess(SUBSCRIPTION_TIER, 'tier', tier)
  }

  const isSubscriptionType = (type: SubscriptionType): boolean => {
    return checkSubscriptionAccess(SUBSCRIPTION_TYPE, 'type', type)
  }

  const isSubscriptionGroup = (group: SubscriptionGroup): boolean => {
    return checkSubscriptionAccess(SUBSCRIPTION_GROUP, 'group', group)
  }

  const isSubscriptionTrialInterval = (
    trialInterval: SubscriptionTrialInterval,
  ): boolean => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_TRIAL_INTERVAL,
      'trialInterval',
      trialInterval,
    )
  }

  const hasObjectPermissions = (
    object: PermissionObject | null | undefined,
    lookingForPermissions: string,
  ): boolean => {
    if (!object || !Object.keys(object).includes('permissions')) {
      return false
    }
    return object.permissions.some(
      (permission) =>
        permission.toLowerCase() === lookingForPermissions.toLowerCase(),
    )
  }

  return {
    accessControl,
    isUserLoggedIn,
    hasAccess,
    hasPaidTeam,
    hasObjectPermissions,
    isActorType,
    isProfileStatus,
    isProfileVisibility,
    hasRole,
    hasTeamRole,
    isSubscriptionBillingCycle,
    isSubscriptionNextBillingPeriodStatus,
    isSubscriptionStatus,
    isSubscriptionStripeConnectionStatus,
    isSubscriptionTier,
    isSubscriptionType,
    isSubscriptionGroup,
    isSubscriptionTrialInterval,
    isMentorStatus,
  }
}
